import { createSignal, Show } from 'solid-js'
import { toast, date, Base64String } from 'common'
import { visitorCheckin as api, DocumentScanResult, AAMVAInfo } from 'st-app/api'
import { requireVisitorCheckinContext } from '../VisitorCheckinContext'
import { requireStepContext } from '../../st-wizard/st-wizard'
import { requestWebcamPhoto } from '../../st-camera-input/st-camera-input'
import Button from '../../st-button/st-button';
import styles from './manual-entry-step.module.css'

const ID_TYPES = ['Passport', 'Out-of-State ID', 'Other'];
const ERROR_ALREADY_CHECKED_IN = 'ALREADY_CHECKED_IN';

// CameraInput returns a full data URL (data:image/png;base64,XXXX). The server
// and the scan-path convention expect RAW base64, so strip the prefix.
const rawB64 = (v?: string): string | undefined =>
    v ? v.replace(/^data:[^;]+;base64,/, '') : undefined;

export const ManualEntryStep = () => {
    const step = requireStepContext();
    const checkin = requireVisitorCheckinContext();

    const [firstName, setFirstName] = createSignal('');
    const [lastName, setLastName] = createSignal('');
    const [dob, setDob] = createSignal('');            // yyyy-mm-dd from <input type=date>
    const [idType, setIdType] = createSignal('Passport');
    const [idNumber, setIdNumber] = createSignal('');
    const [photo, setPhoto] = createSignal<Base64String>('');   // data URL from capture
    const [idImg, setIdImg] = createSignal<Base64String>('');
    const [submitting, setSubmitting] = createSignal(false);

    const dobDate = () => {
        const [y, m, d] = dob().split('-').map(n => parseInt(n, 10));
        if (!y || !m || !d) return undefined;
        const dt = new Date(y, m - 1, d);
        if (dt > new Date()) return undefined;          // no future DOB
        return dt;
    };

    const canSubmit = () => !!firstName().trim() && !!lastName().trim() && !!dobDate() && !submitting();

    const capture = async (aspectRatio: number, set: (v: Base64String) => void) => {
        try {
            const img = await requestWebcamPhoto({ aspectRatio });
            if (img) set(img);
        } catch (e) {
            console.error(e);
        }
    };

    const submit = async () => {
        if (!canSubmit()) {
            toast.notify('error', 'First name, last name, and a valid date of birth are required.');
            return;
        }
        setSubmitting(true);
        try {
            const dt = dobDate()!;
            const photoRaw = rawB64(photo() || undefined);
            const idRaw = rawB64(idImg() || undefined);

            // Build a synthetic scan so Verification + Print run unchanged.
            const aamva = new AAMVAInfo();
            aamva.firstName = firstName().trim();
            aamva.lastName = lastName().trim();
            aamva.dateOfBirth = date.toJson(dt);
            aamva.idNumber = idNumber().trim();
            const scan = new DocumentScanResult();
            scan.AAMVA = aamva;
            if (photoRaw) scan.photoBase64 = photoRaw;
            if (idRaw) scan.idBase64 = idRaw;
            checkin.onScan(scan);

            const resp = await api.RecordManualEntry(checkin.vmGU(), checkin.vmStationGU() ?? '', {
                firstName: aamva.firstName,
                lastName: aamva.lastName,
                dateOfBirth: aamva.dateOfBirth,
                idType: idType(),
                idNumber: aamva.idNumber,
                photoBase64: photoRaw ?? '',
                idBase64: idRaw ?? '',
            });

            if (resp.errorCode === ERROR_ALREADY_CHECKED_IN) {
                checkin.setActiveCheckin(resp.activeCheckin);
                return;
            }
            if (!resp.status) {
                toast.notify('error', 'The manual entry was recorded but no status was returned. Please try again.');
                return;
            }
            if (resp.repeatTier === 'Grace') {
                // Grace: re-verify + reopen operate on the PRIOR record.
                checkin.setRepeatTier('Grace');
                checkin.setRepeatPriorVisitGU(resp.repeatPriorVisit!.vmVisitorGU!);
                checkin.setVmGU(resp.repeatPriorVisit!.vmVisitorGU!);
            } else if (resp.repeatTier === 'Remembered') {
                checkin.setRepeatTier('Remembered');
                const p = resp.repeatPriorVisit!;
                if (p.visitorType) checkin.setVisitorType(p.visitorType);
                if (p.checkInReason) checkin.setReason(p.checkInReason);
                checkin.setMeetingWith(p.meetingWith ?? '');
                checkin.setMeetingWithName(p.meetingWithName ?? '');
            }
            checkin.setVisitorStatus(resp.status as 'PEND' | 'ALLOW' | 'DENY');
            if (resp.status === 'PEND') {
                step.setCompleted(true);
                setTimeout(() => step.next(), 300);
            }
        } catch (e) {
            console.error(e);
            toast.notify('error', 'An error occurred while recording the manual entry. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div class={styles['layout']}>
            <div class={styles['captureCol']}>
                <div class={styles['captureItem']}>
                    <span class={styles['caption']}>Photo</span>
                    <Show when={photo()} fallback={<div class={styles['thumbPlaceholder']}>No photo</div>}>
                        <img class={styles['thumb']} src={photo()} alt="Visitor photo" />
                    </Show>
                    <Button icon='fas fa-camera' onClick={() => capture(1, setPhoto)}>
                        {photo() ? 'Retake' : 'Take Photo'}
                    </Button>
                </div>
                <div class={styles['captureItem']}>
                    <span class={styles['caption']}>ID / Passport</span>
                    <Show when={idImg()} fallback={<div class={styles['thumbPlaceholder']}>No ID image</div>}>
                        <img class={styles['thumb']} src={idImg()} alt="ID image" />
                    </Show>
                    <Button icon='fas fa-camera' onClick={() => capture(1.586, setIdImg)}>
                        {idImg() ? 'Retake' : 'Capture ID'}
                    </Button>
                </div>
                <span class={styles['optional']}>Photo / ID are optional.</span>
            </div>

            <div class={styles['formCol']}>
                <div class={styles['field']}>
                    <label>First Name<span class={styles['req']}>*</span></label>
                    <input type="text" autocomplete="off" autocorrect="off" autocapitalize="words" spellcheck={false} value={firstName()} onInput={(e) => setFirstName(e.currentTarget.value)} />
                </div>
                <div class={styles['field']}>
                    <label>Last Name<span class={styles['req']}>*</span></label>
                    <input type="text" autocomplete="off" autocorrect="off" autocapitalize="words" spellcheck={false} value={lastName()} onInput={(e) => setLastName(e.currentTarget.value)} />
                </div>
                <div class={styles['field']}>
                    <label>Date of Birth<span class={styles['req']}>*</span></label>
                    <input type="date" autocomplete="off" value={dob()} onInput={(e) => setDob(e.currentTarget.value)} />
                </div>
                <div class={styles['field']}>
                    <label>ID Type</label>
                    <select value={idType()} onInput={(e) => setIdType(e.currentTarget.value)}>
                        {ID_TYPES.map(t => <option value={t}>{t}</option>)}
                    </select>
                </div>
                <div class={styles['field']}>
                    <label>ID / Passport Number</label>
                    <input type="text" autocomplete="off" autocorrect="off" spellcheck={false} value={idNumber()} onInput={(e) => setIdNumber(e.currentTarget.value)} />
                </div>

                <div class={styles['actions']}>
                    <Button type='success' icon='fas fa-arrow-right' enabled={canSubmit()} onClick={submit}>Continue</Button>
                </div>

                <Show when={checkin.activeCheckin()}>
                    {(active) => (
                        <div class={styles['alreadyIn']}>
                            <p><strong>{active().firstName} {active().lastName}</strong> is already checked in
                                {active().checkInDatetime ? ` since ${active().checkInDatetime}` : ''}. Check them out and continue?</p>
                            <div style="display:flex; gap:8px;">
                                <Button type='success' icon='fas fa-arrow-right' onClick={async () => {
                                    if (!active().vmVisitorGU) return;
                                    await api.CheckOutExistingVisitor(active().vmVisitorGU!);
                                    checkin.setActiveCheckin(undefined);
                                    await submit();
                                }}>Check Out and Continue</Button>
                                <Button type='danger' icon='fas fa-xmark' onClick={() => checkin.setActiveCheckin(undefined)}>Cancel</Button>
                            </div>
                        </div>
                    )}
                </Show>
            </div>
        </div>
    );
};
